/**
 * audioAlert — utilitário para tocar um alerta sonoro curto via Web Audio API,
 * sem precisar de arquivo de áudio externo.
 *
 * Uso:
 *   import { playOrderAlert } from "../utils/audioAlert";
 *   playOrderAlert();
 *
 * Sons:
 * - `playOrderAlert(repeats=2)` — bipe duplo agudo para "novo pedido"
 *
 * Notas:
 * - Browsers modernos bloqueiam AudioContext até primeira interação do usuário.
 *   Por isso o helper falha silenciosamente quando o contexto não pode iniciar.
 *   O empreendedor abre o painel e clica em algo (filtro, expandir card etc.),
 *   o que libera o autoplay para subsequentes alertas.
 */

let _ctxSingleton = null;

const _getCtx = () => {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  if (!_ctxSingleton) {
    try { _ctxSingleton = new Ctor(); } catch { return null; }
  }
  // Browsers suspendem o contexto entre interações; resume sob demanda.
  if (_ctxSingleton.state === "suspended") {
    try { _ctxSingleton.resume(); } catch { /* ignore */ }
  }
  return _ctxSingleton;
};

const _beep = (ctx, { freq = 880, durationMs = 180, startDelay = 0, volume = 0.18 }) => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = freq;
  const start = ctx.currentTime + startDelay / 1000;
  const stop = start + durationMs / 1000;
  // Envelope simples (attack-release) — evita "clique" no início/fim.
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(volume, start + 0.015);
  gain.gain.setValueAtTime(volume, stop - 0.04);
  gain.gain.exponentialRampToValueAtTime(0.0001, stop);
  osc.connect(gain).connect(ctx.destination);
  osc.start(start);
  osc.stop(stop + 0.02);
};

export const playOrderAlert = (repeats = 2) => {
  const ctx = _getCtx();
  if (!ctx) return;
  try {
    for (let i = 0; i < repeats; i += 1) {
      _beep(ctx, { freq: 880, durationMs: 140, startDelay: i * 260 });
      _beep(ctx, { freq: 1320, durationMs: 140, startDelay: i * 260 + 130 });
    }
  } catch {
    // ignore
  }
};
